import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppActions } from "../../../../store/ducks/app";
import { AtribuirActions } from "../../../../store/ducks/atribuir";
import { DashBoardActions } from "../../../../store/ducks/dashboard";
import { AvaliationActions } from "../../../../store/ducks/avaliation";
import { OperationalDashboardActions } from "../../../../store/ducks/operationalDashboard";
import { handlePaciente } from "../../../../utils/funcs";
import { TextsProps } from "../../../../database/txt.interface";
import moment from "moment";

const PAGE_SIZE = 4;

export interface PatientRecent {
	id: string;
	name: string;
	initials: string;
	photoUrl?: string | null;
	date: string;
	days: number;
	phone: string;
	protocol: string;
	protocolType: string;
	original: any;
}

export const useRecentEvaluationsModalViewModel = (
	isOpen: boolean,
	onClose: () => void
) => {
	const dispatch = useDispatch();
	const indicatorData = useSelector(
		(state: any) => state?.operationalDashboard?.pacientesAvaliados
	);
	const configServicos = useSelector(
		(state: any) => state?.configs?.config_servicos
	);
	const currentLanguage: TextsProps = useSelector(
		(state: any) => state?.configs?.current_language
	);
	const user = useSelector((state: any) => state?.auth?.user);
	const language = useSelector((state: any) => state?.configs?.language);
	const [searchQuery, setSearchQuery] = useState("");
	const [loadingMoreRequested, setLoadingMoreRequested] = useState(false);
	const [sendingReportPatientId, setSendingReportPatientId] = useState<
		string | null
	>(null);
	const navigationTimeout = useRef<NodeJS.Timeout | null>(null);

	useEffect(
		() => () => {
			if (navigationTimeout.current) clearTimeout(navigationTimeout.current);
		},
		[]
	);

	useEffect(() => {
		if (!isOpen) {
			if (navigationTimeout.current) clearTimeout(navigationTimeout.current);
			setSendingReportPatientId(null);
			return;
		}
		setSearchQuery("");
		setLoadingMoreRequested(false);
		setSendingReportPatientId(null);
	}, [isOpen]);

	useEffect(() => {
		if (!isOpen) return;
		const timeout = setTimeout(
			() => {
				setLoadingMoreRequested(false);
				dispatch(
					OperationalDashboardActions.operational_indicator_request({
						indicator: "PACIENTES_AVALIADOS",
						page: 1,
						limit: PAGE_SIZE,
						search: searchQuery,
					})
				);
			},
			searchQuery.trim() ? 300 : 0
		);
		return () => clearTimeout(timeout);
	}, [dispatch, isOpen, searchQuery]);

	useEffect(() => {
		if (!indicatorData?.loading) setLoadingMoreRequested(false);
	}, [indicatorData?.loading]);

	const fetchReportEVA = async (patientId: number) => {
		try {
			const response = await fetch(
				`${process.env.REACT_APP_BASE_URL_V7}syncs/relatorio_eva`,
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						Authorization: "Bearer " + user?.token,
					},
					body: JSON.stringify({
						TOKEN: user?.token,
						txIdioma: language?.toLowerCase?.() || "br",
						ID_USUARIO: patientId,
					}),
				}
			);
			const data = await response.json();
			if (data?.pre_relatorio) {
				dispatch(AvaliationActions.set_data_report_EVA(data));
			}
		} catch (_) {
			// O relatório principal continua disponível quando o EVA não existe.
		}
	};

	const fetchFullPatient = async (patientId: number) => {
		const response = await fetch(
			`${process.env.REACT_APP_BASE_URL_V7}syncs/usuarios_filtro`,
			{
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					Authorization: "Bearer " + user?.token,
				},
				body: JSON.stringify({
					TOKEN: user?.token,
					inicio: 0,
					fim: 1,
					filtros: [
						{ tipo: "ID_USUARIO", valor: [patientId] },
						{ tipo: "USUARIO_EXCLUIDO", valor: "N" },
						{ tipo: "USUARIO_NIVEL", valor: [3] },
					],
				}),
			}
		);
		const data = await response.json();
		return data?.status === "success" && data?.usuarios?.length === 1
			? data.usuarios[0]
			: null;
	};

	const patients: PatientRecent[] = (indicatorData?.items || []).map(
		(patient: any) => {
			const name = patient?.USUARIO_NOME || "";
			const names = name.trim().split(/\s+/).filter(Boolean);
			const lastEvaluation = moment(patient?.ULTIMA_AVALIACAO?.DATA_HORA);
			const type = patient?.ULTIMA_AVALIACAO?.TIPO;
			return {
				id: String(patient?.ID_USUARIO),
				name,
				initials:
					names.length > 1
						? `${names[0].charAt(0)}${names[names.length - 1].charAt(
								0
						  )}`.toUpperCase()
						: name.charAt(0).toUpperCase(),
				photoUrl: patient?.USUARIO_FOTO || null,
				date: lastEvaluation.isValid()
					? lastEvaluation.format("DD/MM/YYYY")
					: "Sem data",
				days: lastEvaluation.isValid()
					? Math.max(moment().diff(lastEvaluation, "days"), 0)
					: 0,
				phone: patient?.USUARIO_CONTATO || "",
				protocol: patient?.ULTIMA_AVALIACAO?.MOVIMENTO || "Geral",
				protocolType:
					type === "PROTOCOLO"
						? "Protocolo"
						: type === "EXERCICIO"
						? "Exercício"
						: "Avaliação",
				original: patient,
			};
		}
	);

	const handleSendReport = async (patientId: string) => {
		if (sendingReportPatientId) return;
		const patient = patients.find((item) => item.id === patientId);
		if (!patient) return;

		const userId = Number(patient.id);
		setSendingReportPatientId(patientId);
		let fullPatient: any = null;
		try {
			fullPatient = await fetchFullPatient(userId);
		} catch (_) {
			setSendingReportPatientId(null);
			return;
		}
		if (!fullPatient) {
			setSendingReportPatientId(null);
			return;
		}

		handlePaciente(fullPatient, dispatch, configServicos);
		dispatch(DashBoardActions.execucoes_request(false));
		dispatch(DashBoardActions.assimetria_request());
		dispatch(DashBoardActions.anamneses_request());
		if (userId) {
			dispatch(DashBoardActions.preensao_palmar_request(userId));
			fetchReportEVA(userId);
		}

		if (navigationTimeout.current) clearTimeout(navigationTimeout.current);
		navigationTimeout.current = setTimeout(() => {
			dispatch(AtribuirActions.set_menu_option_selected(""));
			dispatch(AppActions.set_lista_pacientes([]));
			dispatch(DashBoardActions.set_current_page("emissao_relatorios"));
			dispatch(
				AppActions.set_option_menu(false, currentLanguage?.menu_lateral_inicio)
			);
			setSendingReportPatientId(null);
			onClose();
		}, 1000);
	};

	const handleLoadMore = () => {
		if (indicatorData?.loading || !indicatorData?.paginacao?.temProxima) return;
		setLoadingMoreRequested(true);
		dispatch(
			OperationalDashboardActions.operational_indicator_request({
				indicator: "PACIENTES_AVALIADOS",
				page: (indicatorData.paginacao.pagina || 1) + 1,
				limit: PAGE_SIZE,
				search: searchQuery,
				append: true,
			})
		);
	};

	const retry = () => {
		setLoadingMoreRequested(false);
		dispatch(
			OperationalDashboardActions.operational_indicator_request({
				indicator: "PACIENTES_AVALIADOS",
				page: 1,
				limit: PAGE_SIZE,
				search: searchQuery,
			})
		);
	};

	return {
		searchQuery,
		isLoading: !!indicatorData?.loading,
		isLoadingMore: !!indicatorData?.loading && loadingMoreRequested,
		sendingReportPatientId,
		loadMoreCount: PAGE_SIZE,
		filteredPatients: patients,
		hasExtraPatients: !!indicatorData?.paginacao?.temProxima,
		totalPatients: indicatorData?.total || 0,
		error: indicatorData?.error || null,
		handleSearchChange: setSearchQuery,
		handleSendReport,
		handleLoadMore,
		retry,
	};
};
